import createSchema, { ContactFormSchema } from "src/templates/api/contact/validation";
import { ClassState } from "libs/useClassState";
import { phone } from "phone";

import type { ChangeEventHandler, SubmitEventHandler } from "react";

function createObject<T>(data: ContactFormSchema, value: T): Record<keyof ContactFormSchema, T> {
  return Object.keys(data).reduce<any>((obj, key) => {
    obj[key] = value;
    return obj;
  }, {});
}

class FormData extends ClassState {
  public readonly data: ContactFormSchema;
  public code: string = "USA";

  private readonly validation: Record<keyof ContactFormSchema, boolean>;
  private readonly touched: Record<keyof ContactFormSchema, boolean>;

  constructor(data: ContactFormSchema) {
    super();
    this.data = data;
    this.validation = createObject(data, false);
    this.touched = createObject(data, false);
  }

  public validate = () => {
    const schema = createSchema(this.code);
    const [errors, result] = schema.validate(this.data);
    const failures = errors?.failures();
    for (const key in this.data) {
      this.validation[key as keyof ContactFormSchema] = true;
    }
    for (const failure of failures || []) {
      this.validation[failure.key as keyof ContactFormSchema] = false;
    }
    return result;
  };

  public value(name: keyof ContactFormSchema) {
    return this.data[name];
  }

  private setField(name: keyof ContactFormSchema, value: string) {
    this.data[name] = value as any;
    this.validate();
  }

  public setCode = (code: string) => {
    this.code = code;
    this.update();
  };

  public touch(name: keyof ContactFormSchema) {
    this.touched[name] = true;
  }

  public onBlur(name: keyof ContactFormSchema) {
    return () => this.touch(name);
  }

  public onChange(name: "contact"): ChangeEventHandler<HTMLSelectElement>;
  public onChange(name: keyof ContactFormSchema): ChangeEventHandler<HTMLInputElement>;
  public onChange(name: keyof ContactFormSchema) {
    if (name === "contact") {
      const handler: ChangeEventHandler<HTMLSelectElement> = (e) => {
        this.setField(name, e.target.value);
        this.touch(name);
        this.update();
      };
      return handler;
    }
    if (name === "phone") {
      const handler: ChangeEventHandler<HTMLInputElement> = (e) => {
        const raw = e.target.value;
        const result = phone(raw, { country: this.code });
        const value = result.phoneNumber || raw;
        this.setField(name, value);
        this.touch(name);
        this.update();
      };
      return handler;
    }
    const handler: ChangeEventHandler<HTMLInputElement> = (e) => {
      this.setField(name, e.target.value);
      this.touch(name);
      this.update();
    };
    return handler;
  }

  public isFieldValid(name: keyof ContactFormSchema) {
    return this.touched[name] && this.validation[name];
  }

  public isFieldInvalid(name: keyof ContactFormSchema) {
    return this.touched[name] && !this.validation[name];
  }

  public handleSubmit = (onValid: (data: ContactFormSchema) => any) => {
    const handler: SubmitEventHandler<HTMLFormElement> = (e) => {
      e.preventDefault();
      const result = this.validate();
      if (!result) return;
      onValid(result);
    };
    return handler;
  };
}

export default FormData;
